'use client';

import { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { Link, usePathname } from '@/src/i18n/navigation';
import logo from '@/public/logo.svg';
import MadaPayment from '@/public/mada-payment-logo.svg';
import VisaPayment from '@/public/visa-payment-logo.svg';
import MasterCardPayment from '@/public/mastercard-payment-logo.svg';
import ApplePayLogo from '@/public/apple=pay-payment-logo.svg';
import InstagramLogo from '@/public/instgram.svg';
import TwitterLogo from '@/public/twitter.svg';
import TikTokLogo from '@/public/tiktok.svg';
import { Routes } from '@/src/routes/routes';
import useCartStore from '@/src/lib/state-stores/cartStore';
import SlideUpAnimation from '../animation/SlideUpAnimation';
import { TSession } from '../NavBar';
import LogoIcon from '../icons/Tap/Logo';
import StoreIcon from '../icons/Tap/StoreIcon';
import CartIcon from '../icons/Tap/CartIcon';
import AccountIcon from '../icons/Tap/AccountIcon';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import Container from '../ui/container';

const FooterDesktop = () => {
  const [date, setDate] = useState<number>(2026);
  const t = useTranslations('about-us');
  const locale = useLocale();

  useEffect(() => {
    setDate(new Date().getFullYear());
  }, []);

  return (
    <>
      <footer className="hidden w-full items-center justify-center px-3 py-2 lg:flex">
        <div className="flex w-full max-w-7xl items-center justify-between">
          <div className="flex flex-col">
            <a
              target="_blank"
              href="https://prod-pik.s3.eu-central-1.amazonaws.com/custom/%D8%B4%D9%87%D8%A7%D8%AF%D8%A9+%D8%AA%D8%B3%D8%AC%D9%8A%D9%84+%D9%85%D8%AC%D9%85%D9%88%D8%B9%D8%A9+%D8%B6%D8%B1%D9%8A%D8%A8%D9%8A%D8%A92.PDF"
            >
              <Image
                src={'/vat-logo.svg'}
                alt="vat-logo"
                width={0}
                height={0}
                className="mb-2.25 h-[41.1px] w-8"
              />
            </a>
            <Image
              src={logo}
              alt="pik-logo"
              width={100}
              className="w-20"
              onClick={() => trackEvent(AmplitudeEvents.home_app_clicked)}
            />

            <p className="text-primary">
              {date} {t('jahez')}
            </p>
          </div>

          <div className="flex flex-col items-center gap-y-8.25">
            <div className="flex gap-4">
              <Image src={MadaPayment} alt="mada-logo" className="h-[15.24px] w-[45.7px]" />
              <Image src={VisaPayment} alt="visa-logo" className="h-[15.24px] w-[45.7px]" />
              <Image
                src={MasterCardPayment}
                alt="mastercard-logo"
                className="h-[15.24px] w-[45.7px]"
              />
              <Image src={ApplePayLogo} alt="apple-pay-logo" className="h-[15.24px] w-[45.7px]" />
            </div>
            <div className="flex h-full w-full gap-15">
              <Link
                onClick={() => trackEvent(AmplitudeEvents.about_pik_clicked)}
                href={Routes.aboutUs}
                locale={locale}
                prefetch={false}
              >
                {t('About PIK')}
              </Link>
              <Link
                onClick={() => trackEvent(AmplitudeEvents.terms_and_conditions_clicked)}
                href={Routes.termsAndCondition}
                locale={locale}
                prefetch={false}
              >
                {t('TermsConditions')}
              </Link>
              <Link
                onClick={() => trackEvent(AmplitudeEvents.contact_us_clicked)}
                href={Routes.contactUs}
                locale={locale}
                prefetch={false}
              >
                {t('Contact us')}
              </Link>
              <Link
                onClick={() => trackEvent(AmplitudeEvents.privacy_policy_clicked)}
                href={Routes.privacyPolicy}
                locale={locale}
                prefetch={false}
              >
                {t('Privacy Policy')}
              </Link>
            </div>
          </div>

          <div className="flex flex-col items-center gap-y-10">
            <div className="flex gap-x-5.5">
              <Link href="https://www.instagram.com/pikappsa" target="_blank">
                <Image
                  src={InstagramLogo}
                  alt="instgram-logo"
                  className="h-6 w-6"
                  width={24}
                  height={24}
                />
              </Link>

              <Link href="https://www.tiktok.com/@pikappsa" target="_blank">
                <Image
                  src={TikTokLogo}
                  alt="tiktok-logo"
                  className="h-6 w-6"
                  width={24}
                  height={24}
                />
              </Link>
              <Link href="https://x.com/pikappsa" target="_blank">
                <Image
                  src={TwitterLogo}
                  alt="twitter-logo"
                  className="h-6 w-6"
                  width={24}
                  height={24}
                />
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

type NavItemProps = {
  href: string;
  icon: FC<{ color: string }>;
  label: string;
  isActive: boolean;
  onClick?: () => void;
};

const NavItem = ({ href, icon: Icon, label, isActive, onClick }: NavItemProps) => (
  <div className="relative">
    <Link href={href} prefetch={false} onClick={onClick}>
      <div className="flex flex-col items-center justify-center gap-0.5">
        <Icon color={isActive ? '#1a1a1a' : '#A6A6A6'} />
        <span
          className={`text-[10px] font-medium ${isActive ? 'text-primary font-semibold' : 'text-[#aaaaaa]'}`}
        >
          {label}
        </span>
      </div>
    </Link>
  </div>
);

const FooterMobile: FC<TSession> = ({ session: serverSession }) => {
  const { data: clientSession } = useSession();
  const session = serverSession ?? clientSession;
  const { carts, setCartId } = useCartStore();
  const t = useTranslations('header');

  const currentPath = usePathname();
  const isActive = (path: string) => currentPath === path;

  const hiddenFrom = (paths: string | string[]) => {
    const pathsArray = Array.isArray(paths) ? paths : [paths];

    return pathsArray.some((path) => currentPath.includes(path));
  };

  if (hiddenFrom(['checkout'])) return null;

  return (
    <nav className="shadow-menu fixed right-0 bottom-0 left-0 z-50 flex items-center rounded-t-2xl bg-white/80 pt-2 pb-4 backdrop-blur-xl lg:hidden">
      <Container className="w-full">
        <ul className="flex items-center justify-between font-bold">
          <li>
            <NavItem
              href="/"
              icon={LogoIcon}
              label=""
              isActive={isActive('/')}
              onClick={() => trackEvent(AmplitudeEvents.home_app_clicked)}
            />
          </li>
          <li>
            <NavItem
              href="/stores"
              icon={StoreIcon}
              label={t('stores')}
              isActive={isActive('/stores')}
              onClick={() => trackEvent(AmplitudeEvents.stores_clicked)}
            />
          </li>
          <li className="relative">
            <NavItem
              href="/carts"
              icon={CartIcon}
              label={t('carts')}
              isActive={isActive('/carts')}
              onClick={() => {
                setCartId(undefined);
                trackEvent(AmplitudeEvents.cart_clicked);
              }}
            />
            {session?.user && carts.length ? (
              <div className="centerButton absolute top-[-10%] left-[50%] grid aspect-square min-w-6 place-items-center rounded-full bg-red-500 text-xs text-white">
                <SlideUpAnimation uniqueKey={carts.length}>{carts.length}</SlideUpAnimation>
              </div>
            ) : null}
          </li>

          <li>
            <NavItem
              href="/account"
              icon={AccountIcon}
              label={t('account')}
              isActive={isActive('/account')}
              onClick={() => trackEvent(AmplitudeEvents.my_account_clicked)}
            />
          </li>
        </ul>
      </Container>
    </nav>
  );
};

const Footer = ({ session }: TSession) => {
  return (
    <>
      <FooterDesktop />
      <FooterMobile session={session} />
    </>
  );
};

export default Footer;
